import {captureException} from '@sentry/gatsby';
import {initializeApp} from 'firebase/app';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {getAuth, signInWithCustomToken} from 'firebase/auth';
import {utmSynapse} from 'utm-synapse';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

import {MixpanelParams} from './events';
import {getUserIp} from './storeIp';

type StripeCheckoutSession = {
  storeCountry: string;
  customerEmail?: string;
  lineItems: {
    price: string;
    quantity: number;
  }[];
  successUrl: string;
  cancelUrl: string;
};

export const FIREBASE_CONFIG = {
  projectId: 'the-orca',
  apiKey: 'AIzaSyDKyzvkkb_tECPCAAatqaKtPcmmAugYTFs',
  authDomain: 'the-orca.firebaseapp.com',
};

export type ProductPurchaseStatus = {
  sku: string;
  displayName: string;
  dateOfPurchase: number;
  isSubscriptionInclusive: boolean;
};

export enum RevenuecatStore {
  'APP_STORE' = 'APP_STORE',
  'PLAY_STORE' = 'PLAY_STORE',
}

export enum ExtensionMethod {
  EXTEND_EXISTING = 'EXTEND_EXISTING',
  NEW_PAYMENT = 'NEW_PAYMENT',
}

export type ActivateOfferingForHubActivationPayload = {
  subscriptionExtensionMethod: ExtensionMethod;
};

export type ExtensionPaymentIntent = {
  clientSecret: string;
  ephemeralKey: string;
  customerId: string;
  publishableKey: string;
  havePaymentMethods: boolean;
};

export type GetOfferingResponse = {
  subscriptionExtensionMethod: ExtensionMethod;
  setupIntent: ExtensionPaymentIntent | undefined;
  iapStoreCancellationRequirement: RevenuecatStore[] | undefined;
};

export type CancelGooglePlaySubscriptionPayload = {
  status: string;
};

export type CanGetOfferingResponse = {
  subscriptionExtensionMethod: ExtensionMethod;
  iapStoreCancellationRequirement: RevenuecatStore[] | undefined;
  isEligibleForExtension: boolean;
  extensionExpiryMiliseconds: number;
};

export type GetAccountOfferingsCheckoutSessionResponse = {
  checkoutSession: {
    url: string;
  };
};

export type ActivateHubsForAccountOfferingResponse = {
  status: string;
};

export type GetDiscourseSSOurlInput = {
  sso: string;
  sig: string;
};

export enum SubscriptionState {
  'NONE' = 'NONE',
  'PENDING_ACTIVATION' = 'PENDING_ACTIVATION',
  'ACTIVE' = 'ACTIVE',
  'OVERDUE_RENEWAL' = 'OVERDUE_RENEWAL',
  'BILLING_ISSUE' = 'BILLING_ISSUE',
  'EXPIRED' = 'EXPIRED',
  'CANCELLED' = 'CANCELLED',
}

export enum SubscriptionProvider {
  MOCKED = 'MOCKED',
  REFERRAL = 'REFERRAL',
  INAPP = 'INAPP',
  STRIPE = 'STRIPE',
}

export type SubscriptionStatus = {
  currentPeriodStart: null | number;
  currentPeriodEnd: null | number;
  cancelAtPeriodEnd: boolean;
  isSubscriptionValid: boolean;
  subscriptionPlanId: string;
  subscriptionProvider: SubscriptionProvider;
};

export type AccountPrivilegesResponse = {
  email: string;
  dateCreated: number;
  isCustomer: boolean;
  isFullRefund: boolean;
  isTrailCustomer: boolean;
  dateFirstLogin: number | null;
  dateSubscriptionRenewed: number | null;
  uid: string | null;
  phoneNumber: string | null;
  subscriptionState: SubscriptionState;
  country: string | null;
  hasBetaAccess: boolean;
  subscriptionStatus?: SubscriptionStatus;
  hardwarePurchaseStatus?: {
    products: ProductPurchaseStatus[];
  };
};

const app = initializeApp(FIREBASE_CONFIG);
const functions = getFunctions(app, 'europe-west1'); // use us-central1 for emulators
// connectFunctionsEmulator(functions, 'localhost', 5001);
const auth = getAuth(app);

export const postFacebookEvent = async (
  type: string,
  params: any,
  eventId: string,
  fbc?: string,
  fbp?: string
) => {
  try {
    await httpsCallable(
      functions,
      'postAnalyticsEvent'
    )({
      type,
      params,
      eventId,
      fbc,
      fbp,
    });
  } catch (err) {
    captureException(err);
  }
};

export const getStripeFirebaseDetails = async (id: string) =>
  httpsCallable(
    functions,
    'getStripeOrderDetails'
  )({
    id,
  });

export const getUserIdForEmailOrPhone = async (
  email: string,
  phoneNumber?: string
) =>
  httpsCallable<unknown, {id: string}>(
    functions,
    'getIdForEmailOrPhone'
  )({email, phoneNumber});

export const postMixpanelEvent = async (
  eventName: string,
  deviceId: string,
  uid?: string | null,
  params: Partial<MixpanelParams> = {}
) => {
  const userIp = await getUserIp();

  const utmParams = utmSynapse.load();
  const referral = sessionStorage.getItem('referral_via');

  httpsCallable(
    functions,
    'postMixpanelEvent'
  )({
    eventName,
    deviceId,
    uid,
    params: {
      ...utmParams,
      ...params,
      ...(referral ? {via_referral: referral} : {}),
    },
    userIp,
  });
};

export const getCheckoutSession = async (
  options: StripeCheckoutSession
): Promise<string> => {
  const referral = window?.Rewardful?.referral;
  const urlParams = new URLSearchParams(window?.location?.search);
  const orcaCoupon = urlParams.get('orca-checkout-id');
  const referralCoupon = window?.Rewardful?.coupon?.id;
  const cupon = orcaCoupon || referralCoupon;

  const res = (await httpsCallable(
    functions,
    'createCheckoutSession'
  )({
    ...options,
    clientReferenceId: referral,
    cupon,
  })) as any;

  return res.data as string;
};

type EmailResponseData = {
  status: 'ok' | 'error';
  message: string;
};

export const addEmailToDisplayTwoWaitingList = async (email: string) => {
  const res = await httpsCallable(
    functions,
    'addEmailToDisplayTwoWaitingList'
  )({
    email,
  });

  return res.data as EmailResponseData;
};

export const getAccountPrivileges = async () => {
  const res = await httpsCallable<null, {data: AccountPrivilegesResponse}>(
    functions,
    'getAccountPrivileges'
  )();

  return res.data.data;
};

export type PortalSessionResponse = {
  url: string;
};

export type PricingTableResponse = string;

export const getPortalSessionUrl = async () => {
  const res = await httpsCallable<null, PortalSessionResponse>(
    functions,
    'getStripePortalSessionUrl'
  )();

  return res.data;
};

export const getPricingTableId = async () => {
  const res = await httpsCallable<null, PricingTableResponse>(
    functions,
    'getPricingTable'
  )();

  return res.data;
};

export const callSigninWithCustomToken = async (token: string) => {
  try {
    const result = await signInWithCustomToken(auth, token);
    return result;
  } catch (err) {
    return err;
  }
};

export type LinkAccountPayload = {
  stripeCustomerEmail: string;
  stripeReceiptId: string;
};

export const callLinkAccount = async (payload: LinkAccountPayload) => {
  const res = await httpsCallable<
    LinkAccountPayload,
    {data: AccountPrivilegesResponse}
  >(
    functions,
    'linkAccountToStripePurchase'
  )(payload);

  return res.data;
};

export const callGetRevenuecatSubscription = async () => {
  const res = await httpsCallable(functions, 'getRevenuecatSubscription')();

  return res.data as SubscriptionStatus;
};

type FoundUserId = {uid: string};
type UnknownUserId = {status: string};

type UserIdResponse = FoundUserId & UnknownUserId;

export const callGetUserIdForUserHash = async (hash: string) => {
  const res = await httpsCallable(functions, 'getIdentityForHash')({hash});

  return res.data as UserIdResponse;
};

export const activateAccountOffering = async (
  subscriptionExtensionMethod: ExtensionMethod
) => {
  const res = await httpsCallable(
    functions,
    'activateAccountOffering'
  )({
    subscriptionExtensionMethod,
  });

  return res.data as AccountPrivilegesResponse;
};

export const getAccountOffering = async () => {
  const res = await httpsCallable(functions, 'getAccountOffering')({});

  return res.data as GetOfferingResponse;
};

export const canGetAccountOffering = async () => {
  const res = await httpsCallable(
    functions,
    'getCanActivateAccountOffering'
  )({});

  return res.data as CanGetOfferingResponse;
};

export const getAccountOfferingCheckoutSession = async () => {
  const res = await httpsCallable(
    functions,
    'getAccountOfferingCheckoutSession'
  )({});

  return res.data as GetAccountOfferingsCheckoutSessionResponse;
};

export const activateHubsForAccountOffering = async () => {
  const res = await httpsCallable(
    functions,
    'activateHubsForAccountOffering'
  )({});

  return res.data as ActivateHubsForAccountOfferingResponse;
};

export const updateEmailForSupportedServied = async (
  newEmail: string,
  oldEmail: string
) => {
  await httpsCallable(
    functions,
    'updateUserEmail'
  )({
    oldEmail,
    newEmail,
    skipFirebaseAuth: true,
  });
};

export const getDiscourseSSOurl = async (input: GetDiscourseSSOurlInput) => {
  const res = await httpsCallable(functions, 'getDiscourseSSOurl')(input);
  return res.data as string;
};

export async function updateEmail(newEmail: string, oldEmail: string) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');

  await user.updateEmail(newEmail);
  await updateEmailForSupportedServied(newEmail, oldEmail);
}

export async function updateDisplayName(firstName: string, lastName: string) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const displayName = `${firstName},${lastName}`;
  return user.updateProfile({displayName});
}

export async function removePhoneNumberLink() {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');

  const alreadyLinked = user.providerData.some(
    (p) => p?.providerId === firebase.auth.PhoneAuthProvider.PROVIDER_ID
  );

  if (alreadyLinked) {
    await user.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID);
  }
}

/**
 * Renders (or re-uses) an invisible reCAPTCHA and sends an SMS to link the phone.
 * @returns Firebase ConfirmationResult
 */
export async function startPhoneNumberLink(
  fullPhone: string,
  recaptchaVerifier: firebase.auth.RecaptchaVerifier
) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');

  await removePhoneNumberLink();
  return user.linkWithPhoneNumber(fullPhone, recaptchaVerifier);
}

/**
 * Confirm the SMS code the user received.
 * @param confirmationResult from startPhoneNumberLink
 * @param code the 6-digit SMS code
 */
export async function confirmPhoneNumberLink(
  confirmationResult: firebase.auth.ConfirmationResult,
  code: string
) {
  return confirmationResult.confirm(code);
}
